import {
  // eslint-disable-next-line no-restricted-imports
  useNavigate as useNavigateInternal,
  useLocation,
  NavigateOptions as NavigateOptionsInternal,
  NavigateFunction as NavigateFunctionInternal,
  To,
} from "react-router";

interface NavigateOptions extends NavigateOptionsInternal {
  /** Array of query parameter keys to remove from the URL. */
  searchParamsToRemove?: string[];
}

interface NavigateFunction extends NavigateFunctionInternal {
  (to: To, options?: NavigateOptions): void;
}

/**
 * Calculates route to go to with knowledge of what VW query strings we should keep or remove.
 * @param to - The route to navigate to.
 * @param search - The current search parameters.
 * @param options - The options for the navigation.
 * @returns The new route to navigate to and the options for the navigation.
 */
export const calculateTo = (
  to: To | number,
  search: string,
  options?: NavigateOptions,
) => {
  if (typeof to === "number") {
    return to;
  }

  // Remove search params from the URL if specified by searchParamsToRemove option
  const { searchParamsToRemove, ...optionsInternal } = options || {};
  const currentSearchParams = new URLSearchParams(search);
  searchParamsToRemove?.forEach((param) => {
    currentSearchParams.delete(param);
  });

  const staticSearchParamsToRemove = ["planView", "view"];
  staticSearchParamsToRemove.forEach((param) => {
    currentSearchParams.delete(param);
  });

  // Combine removed search params with new search params when new route is a string
  if (typeof to === "string") {
    const [path, search] = to.split("?");
    const newSearchParams = new URLSearchParams(search);
    const combinedSearchParams = new URLSearchParams([
      ...currentSearchParams,
      ...newSearchParams,
    ]);
    const newTo = path + "?" + combinedSearchParams.toString();
    return { to: newTo, options: optionsInternal };
  }

  // Combine search params from the new route with the current search params
  const newSearchParams = new URLSearchParams(to.search);
  const combinedSearchParams = new URLSearchParams([
    ...currentSearchParams,
    ...newSearchParams,
  ]);
  const newTo = {
    ...to,
    search: combinedSearchParams.toString(),
  };
  return { to: newTo, options: optionsInternal };
};

const useNavigate = () => {
  const navigateInternal = useNavigateInternal();
  const { search } = useLocation();

  /**
   * A navigate function that persists query parameters when navigating to a new route.
   * @param to - The route to navigate to.
   * @param options - The options for the navigation.
   * @param options.searchParamsToRemove - An array of query parameter keys to remove from the URL.
   */
  const navigate: NavigateFunction = (
    to: To | number,
    options?: NavigateOptions,
  ) => {
    const calculatedTo = calculateTo(to, search, options);
    if (typeof calculatedTo === "number") {
      return navigateInternal(calculatedTo);
    }
    return navigateInternal(calculatedTo.to, calculatedTo.options);
  };

  return navigate;
};

export { useNavigate };
